// Real Supabase Auth — password-based sign-in with a forgot-password flow.
// The LoginScreen has three modes: sign-in, forgot-password, and a reset-password
// screen that takes over when the user arrives via a recovery link.

import React from 'react';
import { Icon, Button } from './ui.jsx';
import { Logo } from './Logo.jsx';
import { api } from './api.js';

// ---------- Support contact (centralized so we change once) ----------
// Used in the not-authorized screen and the login footer.
const SUPPORT_EMAIL = 'support@staffologyos.com';

// ---------- RBAC helpers ----------
//
// Two layers of access control:
//
// 1. Base role ('admin' | 'rep') — drives DB row-level security and the
//    coarse split between rep-self-only screens vs. tenant-wide screens.
//
// 2. Permissions (jsonb on user_roles, attached to user.role.permissions
//    when loaded) — drives fine-grained gating in the app: who can approve
//    a run, reject an invoice, invite users, etc. Custom roles like
//    "Approver" or "Sales Manager" are admin-base with restricted perms.
//
// Permission shape:
//   {
//     all: true,                                  // super-shortcut (admin)
//     runs:      { read, create, edit, finalize, approve, reject_invoice, delete },
//     tenants:   { read, write, create },
//     users:     { read, invite, edit, manage_roles },
//     customers: { read, edit, assign_rep },
//     plans:     { read, edit, delete },
//     audit:     { read },
//     own_view:  { read }                         // rep self-service screens
//   }

/**
 * Check a single (module, action) capability against a user's role
 * permissions. Falls back to the base role: 'admin' grants everything,
 * 'rep' only own_view.read.
 */
export function hasPermission(appUser, module, action = 'read') {
  if (!appUser) return false;
  const perms = appUser.role?.permissions || appUser.permissions || null;
  if (perms?.all === true) return true;
  if (perms && perms[module] && perms[module][action] === true) return true;
  // Legacy fallback for users that haven't been assigned a role yet —
  // honor the base 'admin' / 'rep' classification so nothing breaks
  // immediately after the migration.
  if (!perms) {
    const base = appUser.baseRole || appUser.role;
    if (base === 'admin') return true;
    if (base === 'rep')   return module === 'own_view' && action === 'read';
  }
  return false;
}

// Map screen ids to the (module, action) they require. Used by canAccess.
const SCREEN_PERMS = {
  s02:  ['runs',     'read'],     // admin dashboard — KPIs across runs
  s03:  ['runs',     'read'],     // commission runs list
  s04:  ['runs',     'create'],   // new-run wizard step 1
  s05:  ['runs',     'create'],   // upload txn
  s06:  ['runs',     'create'],   // upload aging
  s07:  ['runs',     'create'],   // assign reps
  s08:  ['runs',     'read'],     // run detail
  s09:  ['runs',     'read'],
  s11:  ['customers','read'],
  s11u: ['customers','read'],
  s13:  ['users',    'read'],
  s14:  ['plans',    'read'],
  s14s: ['plans',    'read'],
  s23:  ['plans',    'read'],
  s19:  ['audit',    'read'],
  s20:  ['tenants',  'read'],
  s21:  ['tenants',  'create'],
  s22:  ['users',    'read'],
  s24:  null,                      // Help — open to anyone signed in
  s25:  ['tenants',  'write'],
  s26:  ['users',    'manage_roles'], // Roles management screen
  s15:  ['own_view', 'read'],
  s16:  ['own_view', 'read'],
  s17:  ['own_view', 'read'],
  s18:  ['own_view', 'read'],
};

export function canAccess(appUser, screen) {
  if (!appUser) return false;
  if (screen === 's00') return true;            // login
  const perm = SCREEN_PERMS[screen];
  if (!perm) return true;                       // help / unknown — permissive
  return hasPermission(appUser, perm[0], perm[1]);
}

export function defaultScreenFor(appUser) {
  if (!appUser) return 's00';
  // Pick the first screen the user can see, in priority order.
  const candidates = ['s02', 's03', 's15', 's24'];
  for (const s of candidates) if (canAccess(appUser, s)) return s;
  return 's24';
}

// ---------- Password rules (mirrored from Supabase dashboard config) ----------
// Client-side check is for UX only — Supabase is the source of truth and will
// reject weak passwords at the API level even if we skip this check.
const PASSWORD_RULES = [
  { test: (p) => p.length >= 12,      label: 'At least 12 characters' },
  { test: (p) => /[a-z]/.test(p),      label: 'A lowercase letter (a–z)' },
  { test: (p) => /[A-Z]/.test(p),      label: 'An uppercase letter (A–Z)' },
  { test: (p) => /\d/.test(p),         label: 'A digit (0–9)' },
  { test: (p) => /[^a-zA-Z0-9]/.test(p), label: 'A symbol (!, @, #, …)' },
];
function passwordErrors(password) {
  return PASSWORD_RULES.filter(r => !r.test(password));
}

// ---------- Polish raw Supabase auth errors into product copy ----------
// Supabase returns terse strings like "Invalid login credentials" or
// "For security purposes, you can only request this once every 60 seconds."
// We rewrite the most common ones into language that matches the rest of the
// product. Unknown errors fall through unchanged.
function friendlyAuthError(rawMessage) {
  if (!rawMessage) return 'Something went wrong. Please try again.';
  const msg = String(rawMessage);
  const m = msg.toLowerCase();
  if (m.includes('invalid login credentials')) {
    return 'That email and password didn\'t match. Check your spelling, or use "Forgot password?" below.';
  }
  if (m.includes('email not confirmed')) {
    return 'Your email address hasn\'t been confirmed yet. Check your inbox for the confirmation link, or ask your admin.';
  }
  if (m.includes('rate limit') || m.includes('once every')) {
    return 'Too many attempts. Please wait a minute and try again.';
  }
  if (m.includes('user already registered')) {
    return 'An account with that email already exists. Try signing in instead.';
  }
  if (m.includes('network') || m.includes('failed to fetch')) {
    return 'Couldn\'t reach the server. Check your connection and try again.';
  }
  return msg;
}

// ---------- Small reusable trust block for the login surface ----------
// This is the "feels like a real B2B product" element. We deliberately use
// language that's truthful TODAY (encryption, RLS, session signing) without
// claiming a SOC 2 certification we don't have.
function TrustStrip() {
  const items = [
    { icon: 'lock',     label: 'Encrypted in transit & at rest' },
    { icon: 'override', label: 'Row-level access controls' },
    { icon: 'audit',    label: 'Audit-ready run history' },
  ];
  return (
    <ul aria-label="Security posture" className="grid grid-cols-1 gap-1.5 text-[11.5px] text-neutral-600">
      {items.map((i) => (
        <li key={i.label} className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-neutral-100 text-neutral-700 flex items-center justify-center">
            <Icon name={i.icon} size={11}/>
          </span>
          {i.label}
        </li>
      ))}
    </ul>
  );
}

// ---------- S00 · Login screen ----------
export function LoginScreen() {
  // mode: 'signin' | 'forgot' | 'forgot-sent'
  const [mode, setMode]       = React.useState('signin');
  const [email, setEmail]     = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPw, setShowPw]   = React.useState(false);
  const [status, setStatus]   = React.useState('idle'); // idle | busy
  const [error, setError]     = React.useState(null);

  const submitSignIn = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.includes('@')) { setError('Enter a valid work email.'); return; }
    if (!password) { setError('Enter your password.'); return; }
    setStatus('busy');
    try {
      await api.auth.signInWithPassword(email, password);
      // On success, App.jsx will detect the auth change and route us on.
    } catch (err) {
      setStatus('idle');
      setError(friendlyAuthError(err.message));
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.includes('@')) { setError('Enter a valid work email.'); return; }
    setStatus('busy');
    try {
      await api.auth.resetPassword(email);
      setMode('forgot-sent');
    } catch (err) {
      setError(friendlyAuthError(err.message));
    } finally {
      setStatus('idle');
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-8">
      <div className="w-full max-w-[440px]">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <Logo size={40}/>
          <div>
            <div className="text-[18px] font-bold text-neutral-900 tracking-[-0.01em]">
              Commissions <span className="text-neutral-400 font-normal">by</span> <span className="text-primary-900">Staffology</span>
            </div>
            <div className="text-[12px] text-neutral-500">Commission runs · Approvals · Payroll-ready exports</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
          {mode === 'signin' && (
            <form onSubmit={submitSignIn} noValidate>
              <div className="text-[18px] font-bold text-neutral-900 mb-1">Sign in</div>
              <div className="text-[13px] text-neutral-500 mb-5">Use your work email and password.</div>

              <label htmlFor="login-email" className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Email</label>
              <input
                id="login-email" name="email" type="email"
                autoComplete="email" inputMode="email" autoFocus
                value={email} onChange={e => setEmail(e.target.value)}
                disabled={status === 'busy'}
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
                placeholder="name@company.com"
                className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"/>

              <div className="flex items-center justify-between mt-4 mb-1.5">
                <label htmlFor="login-password" className="block text-[12px] font-semibold text-neutral-700">Password</label>
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  aria-pressed={showPw}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  aria-controls="login-password"
                  className="text-[11px] text-neutral-500 hover:text-neutral-800">
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
              <input
                id="login-password" name="password"
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                value={password} onChange={e => setPassword(e.target.value)}
                disabled={status === 'busy'}
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
                className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"/>

              {error && (
                <div id="login-error" role="alert" aria-live="polite"
                  className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {error}
                </div>
              )}

              <button type="submit" disabled={status === 'busy'}
                className="mt-4 w-full h-10 bg-primary-700 hover:bg-primary-900 text-white text-[14px] font-semibold rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {status === 'busy' && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
                {status === 'busy' ? 'Signing in…' : 'Sign in'}
              </button>

              <div className="mt-4 flex items-center justify-between text-[12px]">
                <button type="button" onClick={() => { setMode('forgot'); setError(null); }}
                  className="text-primary-700 hover:text-primary-900 font-medium">
                  Forgot password?
                </button>
              </div>

              <div className="mt-5 border-t border-neutral-100 pt-4">
                <TrustStrip/>
                <div className="mt-3 text-[11.5px] text-neutral-500 leading-relaxed">
                  Access is restricted to invited team members. If you should have access,
                  contact your Finance / Ops admin or email{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary-700 hover:text-primary-900 font-medium">
                    {SUPPORT_EMAIL}
                  </a>.
                </div>
              </div>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={submitForgot} noValidate>
              <div className="text-[18px] font-bold text-neutral-900 mb-1">Reset your password</div>
              <div className="text-[13px] text-neutral-500 mb-5">We'll email you a link to set a new password.</div>

              <label htmlFor="forgot-email" className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Email</label>
              <input
                id="forgot-email" name="email" type="email"
                autoComplete="email" inputMode="email" autoFocus
                value={email} onChange={e => setEmail(e.target.value)}
                disabled={status === 'busy'}
                aria-invalid={!!error}
                aria-describedby={error ? 'forgot-error' : undefined}
                placeholder="name@company.com"
                className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"/>

              {error && (
                <div id="forgot-error" role="alert" aria-live="polite"
                  className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {error}
                </div>
              )}

              <button type="submit" disabled={status === 'busy'}
                className="mt-4 w-full h-10 bg-primary-700 hover:bg-primary-900 text-white text-[14px] font-semibold rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {status === 'busy' ? 'Sending…' : 'Send reset link'}
              </button>

              <button type="button" onClick={() => { setMode('signin'); setError(null); }}
                className="mt-3 w-full text-[12px] text-neutral-500 hover:text-neutral-800">
                ← Back to sign in
              </button>
            </form>
          )}

          {mode === 'forgot-sent' && (
            <div className="text-center py-4" role="status" aria-live="polite">
              <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center mx-auto mb-3">
                <Icon name="check" size={22}/>
              </div>
              <div className="text-[16px] font-semibold text-neutral-900">Check your email</div>
              <div className="text-[13px] text-neutral-500 mt-1.5 max-w-[320px] mx-auto">
                If an account exists for <b>{email}</b>, we sent a password-reset link. Check your inbox (and spam folder).
              </div>
              <button onClick={() => { setMode('signin'); setPassword(''); }}
                className="mt-5 text-[12px] text-primary-700 hover:text-primary-900 font-medium">
                Back to sign in
              </button>
            </div>
          )}
        </div>

        <div className="text-center text-[11px] text-neutral-400 mt-4 space-x-3">
          <span>© {new Date().getFullYear()} Staffology</span>
          <span aria-hidden="true">·</span>
          <a href="/" className="hover:text-neutral-600">Home</a>
          <span aria-hidden="true">·</span>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-neutral-600">Support</a>
        </div>
      </div>
    </div>
  );
}

// ---------- Reset-password screen ----------
// Shown when the user arrives via the password-reset link. Detection lives in App.jsx.
export function PasswordResetScreen({ onDone }) {
  const [password, setPassword]   = React.useState('');
  const [confirm, setConfirm]     = React.useState('');
  const [showPw, setShowPw]       = React.useState(false);
  const [status, setStatus]       = React.useState('idle');
  const [error, setError]         = React.useState(null);
  const errors = passwordErrors(password);
  // Autofocus the first password field on mount — Codex flagged this as missing.
  const pwRef = React.useRef(null);
  React.useEffect(() => { pwRef.current?.focus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (errors.length > 0) { setError('Password doesn\'t meet the rules below.'); return; }
    if (password !== confirm) { setError('Passwords don\'t match.'); return; }
    setStatus('busy');
    try {
      await api.auth.updatePassword(password);
      onDone?.();
    } catch (err) {
      setError(friendlyAuthError(err.message));
    } finally {
      setStatus('idle');
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-8">
      <div className="w-full max-w-[440px]">
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
          <form onSubmit={submit} noValidate>
            <div className="text-[18px] font-bold text-neutral-900 mb-1">Set a new password</div>
            <div className="text-[13px] text-neutral-500 mb-5">After saving, you'll be signed in.</div>

            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="reset-password" className="block text-[12px] font-semibold text-neutral-700">New password</label>
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                aria-pressed={showPw}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                aria-controls="reset-password"
                className="text-[11px] text-neutral-500 hover:text-neutral-800">
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
            <input
              id="reset-password" name="new-password"
              ref={pwRef}
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              value={password} onChange={e => setPassword(e.target.value)}
              aria-invalid={!!error}
              aria-describedby={error ? 'reset-error password-rules' : 'password-rules'}
              className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>

            <label htmlFor="reset-confirm" className="block text-[12px] font-semibold text-neutral-700 mb-1.5 mt-4">Confirm password</label>
            <input
              id="reset-confirm" name="confirm-password"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirm} onChange={e => setConfirm(e.target.value)}
              aria-invalid={!!error}
              aria-describedby={error ? 'reset-error' : undefined}
              className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>

            {/* Live-updating rule checklist */}
            <div id="password-rules" className="mt-4 border-t border-neutral-100 pt-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1.5">Password requirements</div>
              <ul className="space-y-1">
                {PASSWORD_RULES.map((r, i) => {
                  const ok = r.test(password);
                  return (
                    <li key={i} className={`text-[12px] flex items-center gap-1.5 ${ok ? 'text-emerald-700' : 'text-neutral-500'}`}>
                      <Icon name={ok ? 'check' : 'close'} size={12}/>
                      {r.label}
                    </li>
                  );
                })}
              </ul>
            </div>

            {error && (
              <div id="reset-error" role="alert" aria-live="polite"
                className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                {error}
              </div>
            )}

            <button type="submit" disabled={status === 'busy' || errors.length > 0 || !confirm}
              className="mt-4 w-full h-10 bg-primary-700 hover:bg-primary-900 text-white text-[14px] font-semibold rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {status === 'busy' ? 'Saving…' : 'Save password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------- Not-authorized screen ----------
// Shown when sign-in succeeds at the auth layer but the email isn't in
// public.users for any tenant. Codex flagged the previous version as a
// dead end — added "Try another account" + support contact.
export function NotAuthorizedScreen({ email, onSignOut }) {
  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-8">
      <div className="w-full max-w-[460px] bg-white rounded-xl shadow-sm border border-neutral-200 p-6 text-center">
        <div className="w-14 h-14 rounded-full bg-red-50 border border-red-200 text-red-700 flex items-center justify-center mx-auto mb-4">
          <Icon name="lock" size={22}/>
        </div>
        <div className="text-[18px] font-bold text-neutral-900">Access not yet granted</div>
        <div className="text-[13px] text-neutral-600 mt-1.5 max-w-[360px] mx-auto leading-relaxed">
          You signed in as <b>{email}</b>, but that email isn't on any team's user list yet.
          This usually means an admin still needs to invite you.
        </div>

        <div className="mt-5 text-left bg-neutral-50 border border-neutral-200 rounded-md px-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1.5">What to do</div>
          <ul className="text-[12.5px] text-neutral-700 space-y-1.5">
            <li className="flex items-start gap-2"><Icon name="check" size={12} className="mt-1 text-neutral-500"/><span>Ask your Finance / Ops admin to add this email under <i>Users</i>.</span></li>
            <li className="flex items-start gap-2"><Icon name="check" size={12} className="mt-1 text-neutral-500"/><span>If you have another work email, sign out and try that one.</span></li>
            <li className="flex items-start gap-2"><Icon name="check" size={12} className="mt-1 text-neutral-500"/><span>Still stuck? Email <a href={`mailto:${SUPPORT_EMAIL}?subject=Access%20request%20for%20${encodeURIComponent(email)}`} className="text-primary-700 hover:text-primary-900 font-medium">{SUPPORT_EMAIL}</a> with your company name.</span></li>
          </ul>
        </div>

        <div className="mt-5 flex items-center justify-center gap-2">
          <Button variant="primary" onClick={onSignOut}>Try another account</Button>
          <Button variant="ghost" onClick={onSignOut}>Sign out</Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Deprecated (kept so old imports don't break) ----------
export function loadUser() { return null; }
export function saveUser() {}
export function ForbiddenScreen({ onHome }) {
  return (
    <div className="p-10 text-center">
      <div className="w-14 h-14 rounded-full bg-red-50 border border-red-200 text-red-700 flex items-center justify-center mx-auto mb-4"><Icon name="lock" size={22}/></div>
      <div className="text-[18px] font-bold text-neutral-900">Not authorized</div>
      <div className="text-[13px] text-neutral-500 mt-1.5 max-w-md mx-auto">This page is restricted to admin users.</div>
      <div className="mt-5"><Button variant="primary" onClick={onHome}>Go to your dashboard</Button></div>
    </div>
  );
}
